import React, { useEffect, useState } from 'react'
import { useCheckoutPage } from './checkout-page.hook'
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  LinearProgress,
  Link,
  Typography,
} from '@material-ui/core'
import { PageWrapper } from '../../components/page-wrapper'
import { OpenInNew } from '@material-ui/icons'
import { CheckoutDescription } from './checkout-description'
import { CheckoutSummary } from './checkout-summary'
import { Alert } from '@material-ui/lab'
import { StripeForm } from './stripe-form'
import { ShippingAddressForm } from '../../components/shipping-address-form/shipping-address-form'
import { LoadingState } from '../../store/types'
import { useDispatch } from 'react-redux'
import { resetStore } from '../../store/checkout-page/reducer'
import { useTranslation } from 'react-i18next'
import { useCheckoutPageStyles } from './checkout-page.styles'
import { useAppDialog, useFullPageOverlay } from '../../hooks/use-app-dialog'
import { useNavigate } from 'react-router-dom'
import { useOverlayPreloader } from '../../hooks/use-overlay-preloader'
import { CheckoutPaymentCompleted } from './checkout-payment-completed'
import { useCheckoutBreadcrumbItems } from './use-checkout-breadcrumb-items.hook'

export const CheckoutPage: React.FC = (): React.ReactElement => {
  const props = useCheckoutPage()
  return <CheckoutPageContent {...props} />
}

export type CheckoutPageContentProps = ReturnType<typeof useCheckoutPage>

export const CheckoutPageContent: React.FC<CheckoutPageContentProps> = (
  props
): React.ReactElement => {
  const {
    //props
    itemLoadStatus,
    itemLoadError,
    itemDetails,
    resolvedNonprofitCategoryName,
    resolvedBreadcrumbIds,
    billingDetails,
    orderDetails,
    orderStatus,
    orderError,
    isCurrentUserOwner,
    userInfo,
    isValidAddress,
    isShowAlert,

    //methods
    onPaymentMethodReceivedHandler,
    isShowStripeForm,
  } = props

  const { t } = useTranslation()
  const classes = useCheckoutPageStyles()
  const dispatch = useDispatch()
  const { setAppDialog, setIsShow } = useAppDialog()
  const navigate = useNavigate()
  const { setShowFullPageOverlay } = useFullPageOverlay()
  const { setIsShowOverlayPreloader } = useOverlayPreloader()
  const [paymentGood, setPaymentGood] = useState(false)
  const [addAddressButtonClicked, setAddAddressButtonClicked] = useState(false)

  const checkoutBreadcrumbItems = useCheckoutBreadcrumbItems({
    itemDetails,
    resolvedNonprofitCategoryName,
    resolvedBreadcrumbIds,
  })

  const onSearchHandler = (): void => {
    navigate('/search')
  }

  const onDonateHandler = (): void => {
    navigate('/donate')
  }

  useEffect(() => {
    setIsShowOverlayPreloader &&
      setIsShowOverlayPreloader(orderStatus === LoadingState.LOADING)
  }, [orderStatus])

  //Reset Store
  useEffect(() => {
    return () => {
      dispatch(resetStore())
      setIsShow(false)
      setShowFullPageOverlay(false)
    }
  }, [])

  //Order Status Handler
  useEffect(() => {
    //Success Handler
    if (orderStatus === LoadingState.LOADED && orderDetails)
      setPaymentGood(true)

    //Error Handler
    orderStatus === LoadingState.ERROR &&
      setAppDialog({
        isShow: true,
        variant: 'error',
        title: t('checkout.errorDialog.title'),
        message: (
          <>
            <div>{t('checkout.errorDialog.content')}</div>
            <Typography color="error">
              {orderError?.getErrorMessage(t)}
            </Typography>
          </>
        ),
      })
  }, [orderStatus])

  if (!billingDetails) return <Error>{t('checkout.errorUnauthorized')}</Error>
  if (itemLoadStatus === LoadingState.ERROR)
    return <Error>{itemLoadError?.getErrorMessages(t)}</Error>
  if (isCurrentUserOwner)
    return <Error>{t('checkout.currentUserIsOwner')}</Error>
  if (itemLoadStatus !== LoadingState.LOADED) return <LinearProgress />

  let helpSiteSuffix = ''

  if (process.env.REACT_APP_HELP_SITE_BASE === undefined) helpSiteSuffix = ''
  else if (process.env.REACT_APP_HELP_SITE_BASE.endsWith('/'))
    helpSiteSuffix = 'link-to-policies'
  else helpSiteSuffix = '/link-to-policies'

  const linkToPoliciesUrl =
    process.env.REACT_APP_HELP_SITE_BASE + helpSiteSuffix

  //Alert show
  const alertShow = () => {
    if (userInfo.shippingAddressExists === false) {
      return true
    } else {
      return isShowAlert
    }
  }

  //Alert Text
  const alertText = () => {
    if (userInfo.shippingAddressExists === false) {
      return (
        <>
          {t('checkout.addAddresssLabel')}{' '}
          <b className={classes.boldLink}>
            <Button
              color="primary"
              variant="text"
              className={classes.addAddressButton}
              onClick={() => setAddAddressButtonClicked(true)}
            >
              {t('checkout.addAddressLink')}
            </Button>
          </b>
        </>
      )
    } else {
      return isValidAddress
        ? t('checkout.shippingAddressValid')
        : t('checkout.shippingAddressInvalid')
    }
  }

  //Alert Status
  const alertStatus = () => {
    if (userInfo.shippingAddressExists === false) {
      return 'warning'
    } else {
      return isValidAddress ? 'success' : 'error'
    }
  }

  const pageTitle =
    paymentGood && props.orderDetails
      ? t('checkout.successDialog.title')
      : t('checkout.pageTitle')

  return (
    <>
      <ShippingAddressForm
        addAddressButtonClicked={addAddressButtonClicked}
        setAddAddressButtonClicked={setAddAddressButtonClicked}
        onClose={() => setAddAddressButtonClicked(false)}
      />
      <PageWrapper
        title={pageTitle}
        displayGoBackButton={false}
        breadcrumbItems={checkoutBreadcrumbItems}
      >
        {paymentGood && props.orderDetails ? (
          <CheckoutPaymentCompleted
            receiptUrl={
              props.orderDetails.data ? props.orderDetails.data.receiptUrl : ''
            }
            onSearch={onSearchHandler}
            onDonate={onDonateHandler}
          />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {alertShow() && (
                <Alert severity={alertStatus()}>
                  {alertText()}
                </Alert>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={7}
              className={classes.descriptionArea}
            >
              <CheckoutDescription />
            </Grid>

            <Grid item xs={12} sm={6} md={5}>
              <Typography
                variant="caption"
                component="div"
                style={{
                  display: 'block',
                  marginBottom: 8,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                }}
              >
                {t('checkout.orderSummary')}
              </Typography>
              <Card style={{ marginBottom: 24 }}>
                <CheckoutSummary />
              </Card>

              {isShowStripeForm() && (
                <>
                  <Typography
                    variant="caption"
                    component="div"
                    style={{
                      display: 'block',
                      marginBottom: 8,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('checkout.payment')}
                  </Typography>
                  <Card>
                    <StripeForm
                      onPaymentMethodReceived={onPaymentMethodReceivedHandler}
                      billingDetails={billingDetails}
                      disabled={
                        [LoadingState.LOADING, LoadingState.LOADED].indexOf(
                          orderStatus
                        ) !== -1
                      }
                    />
                  </Card>
                </>
              )}

              <div className={classes.linksArea}>
                <Link href={linkToPoliciesUrl} className={classes.linkInNew}>
                  {t('checkout.linkToPolices')}
                  <OpenInNew />
                </Link>
              </div>
            </Grid>
          </Grid>
        )}
      </PageWrapper>
    </>
  )
}

const Error: React.FC<{ children?: React.ReactNode }> = (
  props
): React.ReactElement => {
  return (
    <Container>
      <Box marginTop={3}>
        <Alert color="error">{props.children}</Alert>
      </Box>
    </Container>
  )
}
